import { cn } from "@repo/shared/utils";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/Base/Dialog";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { CircleHelpIcon } from "lucide-react";

export const MemberClassDefinitionsDialog = ({
  open,
  setOpen,
  triggerStyle,
  iconStyle,
}: {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  triggerStyle?: string;
  iconStyle?: string;
}) => {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!setOpen && (
        <Tooltip label="Member Classes" asChild>
          <DialogTrigger
            className={cn(
              "w-10 h-10 flex flex-row justify-center items-center self-end",
              triggerStyle
            )}
          >
            <CircleHelpIcon className={cn("text-primary", iconStyle)} />
          </DialogTrigger>
        </Tooltip>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Definitions and Permissions of Member Classes
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* Voting */}
          <h2 className="text-lg font-bold mb-1">Voting</h2>
          <p className="mb-2">
            Team members with the <em>Voting</em> class are considered full
            voting members. When running a meeting for this team, voting members
            appear in the Roll Call section, and will also appear in the meeting
            minutes, even when absent.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Committee/board members</li>
          </ul>

          {/* Tie Breaker */}
          {/* <h2 className="text-lg font-bold mb-1">Tie Breaker</h2>
          <p className="mb-2">
            Team members with <em>Tie Breaker</em> status are members who can
            only vote to break a tie.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Mayors</li>
          </ul> */}

          {/* Conditional Voters */}
          {/* <h2 className="text-lg font-bold mb-1">Conditional Voters</h2>
          <p className="mb-2">
            <em>Conditional Voters</em> are team members that are only allowed
            to vote on specific topics based on their department.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>County Salary Board Department Heads</li>
          </ul> */}

          <p className="mb-2 p-3 rounded-md bg-black/10">
            <strong>Voting Member Permissions:</strong> All voting members are
            also permitted to view confidential documents attached to meeting
            agendas, and they are also permitted to run a meeting for the team.
          </p>

          <hr className="pb-5 mt-5" />

          {/* Non-voting */}
          <h2 className="text-lg font-bold mb-1">Non-voting</h2>
          <p className="mb-2">
            Team members with the <em>Non-voting</em> class are not considered
            board members, but are considered part of the meeting. These members
            still appear in the Roll Call panel when running a meeting, but are
            listed separate from voting members.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Secretary</li>
            <li>Department Heads who are expected to attend</li>
          </ul>

          {/* Support Only */}
          <h2 className="text-lg font-bold mb-1">Support Only</h2>
          <p className="mb-2">
            Team members that are considered <em>Support Only</em> are intended
            to have access to the team, but not considered part of meetings.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Employees</li>
            <li>Contracted personnel who are not expected to attend</li>
          </ul>
          <p className="mb-2">
            Support-only members do not appear in the Roll Call when running a
            meeting, and they are not permitted to view confidential attachments
            nor run meetings for the team.
          </p>

          <p className="mb-2 p-3 rounded-md bg-black/10">
            <strong>Non-voting Member Permissions:</strong> Non-voting members
            of any class are not permitted to view confidential agenda
            attachments nor run meetings.
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
